import { ORGANIZATION_ROLES, type OrganizationRole } from '@/enums/organization-roles.enum.ts';

export type OrgElevatedCategory = 'management' | 'events' | 'content';

export const ORG_ELEVATED_CATEGORY_ORDER: OrgElevatedCategory[] = ['management', 'events', 'content'];

const ROLE_CATEGORY: Partial<Record<OrganizationRole, OrgElevatedCategory>> = {
  [ORGANIZATION_ROLES.OWNER]: 'management',
  [ORGANIZATION_ROLES.ADMIN]: 'management',
  [ORGANIZATION_ROLES.PROCUREMENT]: 'management',
  [ORGANIZATION_ROLES.EVENT_PLANNER]: 'events',
  [ORGANIZATION_ROLES.EVENT_ORGANIZER]: 'events',
  [ORGANIZATION_ROLES.CONTENT_MANAGER]: 'content',
};

const RANK: Partial<Record<OrganizationRole, number>> = {
  [ORGANIZATION_ROLES.OWNER]: 3,
  [ORGANIZATION_ROLES.ADMIN]: 2,
  [ORGANIZATION_ROLES.PROCUREMENT]: 1,
  [ORGANIZATION_ROLES.EVENT_PLANNER]: 2,
  [ORGANIZATION_ROLES.EVENT_ORGANIZER]: 1,
  [ORGANIZATION_ROLES.CONTENT_MANAGER]: 1,
};

export function orgElevatedCategoryOf(role: OrganizationRole): OrgElevatedCategory | null {
  return ROLE_CATEGORY[role] ?? null;
}

export function orgElevatedRank(role: OrganizationRole): number {
  return RANK[role] ?? 0;
}

/** One role per category: keep highest rank in each category. */
export function normalizeOrgElevatedDraft(roles: OrganizationRole[]): OrganizationRole[] {
  const by: Partial<Record<OrgElevatedCategory, OrganizationRole>> = {};
  for (const r of roles) {
    const c = orgElevatedCategoryOf(r);
    if (!c) {
      continue;
    }
    const cur = by[c];
    if (!cur || orgElevatedRank(r) > orgElevatedRank(cur)) {
      by[c] = r;
    }
  }
  return ORG_ELEVATED_CATEGORY_ORDER.flatMap((c) => (by[c] ? [by[c]!] : []));
}

export function rolesByOrgElevatedCategory(
  roles: OrganizationRole[],
): Partial<Record<OrgElevatedCategory, OrganizationRole>> {
  const by: Partial<Record<OrgElevatedCategory, OrganizationRole>> = {};
  for (const r of roles) {
    const c = orgElevatedCategoryOf(r);
    if (!c) {
      continue;
    }
    const cur = by[c];
    if (!cur || orgElevatedRank(r) > orgElevatedRank(cur)) {
      by[c] = r;
    }
  }
  return by;
}

export const ORG_ELEVATED_SECTION_ROLES: Record<OrgElevatedCategory, readonly OrganizationRole[]> = {
  management: [ORGANIZATION_ROLES.OWNER, ORGANIZATION_ROLES.ADMIN, ORGANIZATION_ROLES.PROCUREMENT],
  events: [ORGANIZATION_ROLES.EVENT_PLANNER, ORGANIZATION_ROLES.EVENT_ORGANIZER],
  content: [ORGANIZATION_ROLES.CONTENT_MANAGER],
};
